import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaPhoneAlt, FaWhatsapp, FaBars, FaTimes } from "react-icons/fa";
import { SITE_CONTENT } from "../content";

const Header = () => {
  const { logoText, logoImage } = SITE_CONTENT.global;
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: "Home", to: "/" },
    { label: "Services", href: "#services" },
    { label: "Menu", to: "/menu" },
    { label: "Coverage", href: "#service-areas" },
    { label: "Reviews", href: "#testimonials" },
    { label: "FAQ", href: "#faq" },
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
  ];

  const handleLinkClick = (e, item) => {
    if (item.href) {
      if (window.location.pathname !== "/") {
        // Normal navigation for subpages
      } else {
        e.preventDefault();
        const target = document.querySelector(item.href);
        if (target) {
          target.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#06140b]/95 backdrop-blur-md shadow-xl border-b border-[#20452d]/40 py-2.5"
            : "bg-gradient-to-b from-[#051109]/90 to-transparent py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="relative p-0.5 rounded-full bg-gradient-to-tr from-[#D4AF37] to-[#E8A040] shadow-md shadow-amber-500/10">
              <img
                src={logoImage}
                alt={logoText}
                className="h-10 sm:h-12 w-auto object-contain rounded-full bg-white p-0.5"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              link.href ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleLinkClick(e, link)}
                  className="text-xs font-medium tracking-[0.14em] uppercase text-white/80 hover:text-[#E8A040] transition-colors py-2 relative group"
                >
                  {link.label}
                  <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-[#D4AF37] to-[#E8A040] transition-all duration-300 group-hover:w-full"></span>
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className="text-xs font-medium tracking-[0.14em] uppercase text-white/80 hover:text-[#E8A040] transition-colors py-2 relative group"
                >
                  {link.label}
                  <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-[#D4AF37] to-[#E8A040] transition-all duration-300 group-hover:w-full"></span>
                </Link>
              )
            ))}
          </nav>

          {/* Header Actions (Call, WhatsApp, Quote) */}
          <div className="hidden sm:flex items-center gap-3">
            <a
              href="https://wa.me/919150299336?text=Hi%20Nawabi%20Caterers,%20I%20would%20like%20to%20discuss%20catering%20for%20my%20event."
              target="_blank"
              rel="noreferrer"
              className="p-2.5 rounded-full bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366] hover:text-white transition-all duration-300 border border-[#25D366]/30"
              title="Chat on WhatsApp"
            >
              <FaWhatsapp className="w-4 h-4" />
            </a>
            <a
              href="tel:+919150299336"
              className="p-2.5 rounded-full bg-white/10 text-white/90 hover:bg-[#E8A040] hover:text-[#0a1a0f] transition-all duration-300 border border-white/20"
              title="Call Us"
            >
              <FaPhoneAlt className="w-3.5 h-3.5" />
            </a>
            <a
              href="#booking-section"
              className="px-5 py-2.5 bg-gradient-to-r from-[#D4AF37] to-[#E8A040] text-[#0a1a0f] text-xs font-semibold tracking-[0.14em] uppercase rounded-full shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-105 transition-all duration-300"
            >
              Request Quote
            </a>
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2.5 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Toggle Navigation"
          >
            {mobileMenuOpen ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer Menu */}
      <div
        className={`fixed inset-0 z-40 bg-black/70 backdrop-blur-md transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <div
          className={`fixed top-0 right-0 w-[82%] max-w-sm h-full bg-[#07170d] border-l border-[#20452d] p-6 pt-20 flex flex-col justify-between transition-transform duration-300 ${
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col gap-1">
            <div className="mb-4 pb-4 border-b border-white/10">
              <span className="text-[#22c55e] text-xs font-bold tracking-widest uppercase flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-ping"></span>
                100% Halal Outdoor Catering
              </span>
              <p className="text-white/60 text-xs mt-1">Salem · Chennai · Coimbatore & All TN</p>
            </div>

            {navLinks.map((link) => (
              link.href ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleLinkClick(e, link)}
                  className="px-4 py-3 rounded-lg text-sm uppercase tracking-wider font-medium text-white/90 hover:bg-[#132c1c] hover:text-[#E8A040] transition-colors"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg text-sm uppercase tracking-wider font-medium text-white/90 hover:bg-[#132c1c] hover:text-[#E8A040] transition-colors"
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          <div className="pt-6 border-t border-white/10 flex flex-col gap-3">
            <a
              href="tel:+919150299336"
              className="flex items-center justify-center gap-2 py-3 bg-[#132d1d] border border-[#235033] rounded-xl text-white text-sm font-medium hover:bg-[#1a3d27]"
            >
              <FaPhoneAlt className="text-[#E8A040] w-3.5 h-3.5" />
              +91 91502 99336
            </a>
            <a
              href="https://wa.me/919150299336?text=Hi%20Nawabi%20Caterers,%20I%20would%20like%20to%20discuss%20catering%20for%20my%20event."
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 py-3 bg-[#25D366] text-white rounded-xl text-sm font-semibold hover:bg-[#20ba59]"
            >
              <FaWhatsapp className="w-4 h-4" />
              WhatsApp Us
            </a>
            <a
              href="#booking-section"
              onClick={() => setMobileMenuOpen(false)}
              className="py-3 text-center bg-gradient-to-r from-[#D4AF37] to-[#E8A040] text-[#0a1a0f] rounded-xl text-sm font-bold uppercase tracking-wider"
            >
              Request a Catering Quote
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
